// JWT-based admin authentication via the PHP REST API.
// Replaces the previous Firebase auth integration.

import { api, getToken, setToken, clearToken } from "./client";

function decodePayload(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

function userFromToken(token) {
  const payload = decodePayload(token);
  if (!payload || payload.exp * 1000 <= Date.now()) return null;
  // Tokens issued before team accounts existed carry sub "admin" and no role;
  // they belong to the original admin.
  const sub = String(payload.sub ?? "admin");
  return {
    uid: sub,
    email: payload.email,
    displayName: payload.name || "",
    role: payload.role || (sub === "admin" ? "admin" : "member"),
  };
}

export async function isSetupComplete() {
  const { complete } = await api.get("/auth/setup");
  return Boolean(complete);
}

// First-time setup: creates the admin account and returns a JWT.
export async function createAdmin({ email, password, displayName }) {
  const { token, user } = await api.post("/auth/setup", { email, password, displayName });
  setToken(token);
  window.dispatchEvent(new CustomEvent("vo:auth-change"));
  return user;
}

export async function login({ email, password }) {
  const { token, user } = await api.post("/auth/login", { email, password });
  setToken(token);
  window.dispatchEvent(new CustomEvent("vo:auth-change"));
  return user;
}

export function logout() {
  clearToken();
  window.dispatchEvent(new CustomEvent("vo:auth-change"));
}

// Subscribe to auth-state changes. Calls cb(user) immediately and again after
// any login or logout. Returns an unsubscribe function (mirrors Firebase API).
export function watchAuth(cb) {
  const token = getToken();
  const user  = token ? userFromToken(token) : null;

  // If the stored token is expired, clean it up.
  if (token && !user) clearToken();

  // Fire asynchronously so the caller's useEffect can register the unsub first.
  setTimeout(() => cb(user), 0);

  const handler = () => {
    const t = getToken();
    cb(t ? userFromToken(t) : null);
  };
  window.addEventListener("vo:auth-change", handler);
  return () => window.removeEventListener("vo:auth-change", handler);
}

// Forgot password: email a one-time code, then exchange it for a new password.
export async function requestPasswordReset(email) {
  await api.post("/auth/forgot", { email });
}

export async function resetPassword({ email, otp, password }) {
  await api.post("/auth/reset", { email, otp, password });
}

// Signed-in user edits their own name and/or password. The server returns a
// fresh token so the new name shows up straight away.
export async function updateMyAccount({ name, currentPassword, newPassword }) {
  const payload = { name: String(name || "").trim() };
  if (newPassword) {
    payload.currentPassword = currentPassword || "";
    payload.newPassword = newPassword;
  }
  const { token } = await api.put("/auth/me", payload, { auth: true });
  setToken(token);
  window.dispatchEvent(new CustomEvent("vo:auth-change"));
}
